// src/components/resources/ResourcesControls.js
// Sticky controls bar with search, content type tabs, and sort

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Search, X, ChevronDown, SlidersHorizontal, RotateCcw } from 'lucide-react';
import {
  CONTENT_TYPES,
  CONTENT_TYPE_LABELS,
  SORT_LABELS,
} from '../../hooks/useResourceFilters';
import './styles/resources-controls.css';

const ResourcesControls = ({
  search,
  onSearchChange,
  contentType,
  onContentTypeChange,
  sortOrder,
  onSortChange,
  filteredCount,
  totalCount,
  countsByType,
  hasActiveFilters,
  onClearFilters,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const controlsRef = useRef(null);
  const sentinelRef = useRef(null);
  const sortRef = useRef(null);

  const prefersReducedMotion = useReducedMotion();

  // Sticky detection using IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get count for a content type
  const getCount = (type) => {
    if (type === CONTENT_TYPES.ALL) return countsByType?.all || totalCount;
    return countsByType?.[type] || 0;
  };

  return (
    <>
      {/* Sentinel for sticky detection */}
      <div ref={sentinelRef} className="resources-controls__sentinel" />

      <div
        ref={controlsRef}
        className={`resources-controls ${isSticky ? 'resources-controls--sticky' : ''}`}
      >
        <div className="resources-controls__container">
          <div className="resources-controls__inner">
            {/* Search */}
            <div className={`resources-controls__search ${searchFocused ? 'resources-controls__search--focused' : ''}`}>
              <Search size={18} className="resources-controls__search-icon" />
              <input
                type="text"
                placeholder="Search articles, case studies..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="resources-controls__search-input"
              />
              <AnimatePresence>
                {search && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15 }}
                    className="resources-controls__search-clear"
                    onClick={() => onSearchChange('')}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Content Type Tabs */}
            <div className="resources-controls__tabs">
              {Object.entries(CONTENT_TYPE_LABELS).map(([type, label]) => (
                <button
                  key={type}
                  className={`resources-controls__tab ${
                    contentType === type ? 'resources-controls__tab--active' : ''
                  }`}
                  onClick={() => onContentTypeChange(type)}
                  aria-pressed={contentType === type}
                >
                  <span className="resources-controls__tab-label">{label}</span>
                  <span className="resources-controls__tab-count">{getCount(type)}</span>
                  {contentType === type && !prefersReducedMotion && (
                    <motion.div
                      className="resources-controls__tab-indicator"
                      layoutId="tab-indicator"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Right side: Sort + Clear */}
            <div className="resources-controls__actions">
              {/* Sort Dropdown */}
              <div className="resources-controls__sort" ref={sortRef}>
                <button
                  className={`resources-controls__sort-trigger ${
                    showSortDropdown ? 'resources-controls__sort-trigger--open' : ''
                  }`}
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  aria-expanded={showSortDropdown}
                  aria-haspopup="listbox"
                >
                  <SlidersHorizontal size={15} />
                  <span>{SORT_LABELS[sortOrder]}</span>
                  <ChevronDown
                    size={14}
                    className={`resources-controls__sort-chevron ${
                      showSortDropdown ? 'resources-controls__sort-chevron--open' : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {showSortDropdown && (
                    <motion.div
                      className="resources-controls__sort-dropdown"
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
                      role="listbox"
                    >
                      {Object.entries(SORT_LABELS).map(([value, label]) => (
                        <button
                          key={value}
                          className={`resources-controls__sort-option ${
                            sortOrder === value ? 'resources-controls__sort-option--active' : ''
                          }`}
                          onClick={() => {
                            onSortChange(value);
                            setShowSortDropdown(false);
                          }}
                          role="option"
                          aria-selected={sortOrder === value}
                        >
                          <span>{label}</span>
                          {sortOrder === value && (
                            <div className="resources-controls__sort-check" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Clear Filters */}
              <AnimatePresence>
                {hasActiveFilters && (
                  <motion.button
                    className="resources-controls__clear"
                    onClick={onClearFilters}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  >
                    <RotateCcw size={14} />
                    <span>Clear</span>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Results count - mobile only */}
          <div className="resources-controls__results">
            <span className="resources-controls__results-count">{filteredCount}</span>
            <span className="resources-controls__results-label">
              {filteredCount === 1 ? 'result' : 'results'}
              {filteredCount !== totalCount && (
                <span className="resources-controls__results-total"> of {totalCount}</span>
              )}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourcesControls;
